import React from 'react';
import { XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';

interface UpdateTokenModalProps {
  showModal: boolean;
  onClose: () => void;
  updateForm: {
    name: string;
    domains: string;
    expirationDate: string;
    readPermission: boolean;
    writePermission: boolean;
    status: boolean;
  };
  onFormChange: (field: keyof UpdateTokenModalProps['updateForm'], value: any) => void; // Fixed type
  onSubmit: () => void;
  showExpirationDateInput: boolean;
  setShowExpirationDateInput: (show: boolean) => void;
  updateDomainTags: string[];
  setUpdateDomainTags: (tags: string[]) => void;
  updateDomainInput: string;
  setUpdateDomainInput: (input: string) => void;
  updateDomainError: string;
  setUpdateDomainError: (error: string) => void;
  editingDomainIdx: number | null;
  setEditingDomainIdx: (idx: number | null) => void;
  editingDomainValue: string;
  setEditingDomainValue: (value: string) => void;
}

const UpdateTokenModal: React.FC<UpdateTokenModalProps> = ({
  showModal,
  onClose,
  updateForm,
  onFormChange,
  onSubmit,
  showExpirationDateInput,
  setShowExpirationDateInput,
  updateDomainTags,
  setUpdateDomainTags,
  updateDomainInput,
  setUpdateDomainInput,
  updateDomainError,
  setUpdateDomainError,
  editingDomainIdx,
  setEditingDomainIdx,
  editingDomainValue,
  setEditingDomainValue,
}) => {
  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Update Token</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Token Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Token Name
            </label>
            <input
              type="text"
              value={updateForm.name}
              onChange={(e) => onFormChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={updateForm.status ? "active" : "inactive"}
              onChange={e => onFormChange('status', e.target.value === "active")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Allowed Domains */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allowed Domains
            </label>
            <div className="flex flex-wrap items-center border border-gray-300 rounded-md p-2 space-x-2">
              <div className="flex flex-wrap items-center gap-2">
                {updateDomainTags.map((tag, idx) => (
                  <div key={idx} className="flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm">
                    {editingDomainIdx === idx ? (
                      <input
                        type="text"
                        value={editingDomainValue}
                        autoFocus
                        onChange={e => setEditingDomainValue(e.target.value)}
                        onBlur={() => {
                          if (
                            editingDomainValue.trim() &&
                            !updateDomainTags.includes(editingDomainValue.trim())
                          ) {
                            const newTags = [...updateDomainTags];
                            newTags[idx] = editingDomainValue.trim();
                            setUpdateDomainTags(newTags);
                          }
                          setEditingDomainIdx(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === 'Tab') {
                            e.preventDefault();
                            if (
                              editingDomainValue.trim() &&
                              !updateDomainTags.includes(editingDomainValue.trim())
                            ) {
                              const newTags = [...updateDomainTags];
                              newTags[idx] = editingDomainValue.trim();
                              setUpdateDomainTags(newTags);
                            }
                            setEditingDomainIdx(null);
                          }
                          if (e.key === 'Escape') {
                            setEditingDomainIdx(null);
                          }
                        }}
                        className="border-none outline-none bg-blue-100 text-blue-700 px-1"
                      />
                    ) : (
                      <>
                        <span
                          onClick={() => {
                            setEditingDomainIdx(idx);
                            setEditingDomainValue(tag);
                          }}
                          className="cursor-pointer"
                          title="Click to edit"
                        >
                          {tag}
                        </span>
                        <button
                          type="button"
                          className="ml-2 text-blue-500 hover:text-blue-700"
                          onClick={() => {
                            setUpdateDomainTags(updateDomainTags.filter((_, i) => i !== idx));
                            setUpdateDomainError('');
                          }}
                        >
                          &times;
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <input
                type="text"
                value={updateDomainInput}
                onChange={e => setUpdateDomainInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && updateDomainInput.trim() !== '') {
                    e.preventDefault();
                    if (updateDomainTags.length >= 5) {
                      setUpdateDomainError('You can only add up to 5 domains.');
                      return;
                    }
                    if (!updateDomainTags.includes(updateDomainInput.trim())) {
                      setUpdateDomainTags([...updateDomainTags, updateDomainInput.trim()]);
                      setUpdateDomainInput('');
                      setUpdateDomainError('');
                    }
                  }
                }}
                placeholder="Add a domain and press Enter"
                className="flex-1 border-none outline-none focus:ring-0"
              />
            </div>
            {updateDomainError && <p className="mt-2 text-sm text-red-500">{updateDomainError}</p>}
            <p className="mt-1 text-xs text-gray-500">You can add up to 5 domains.</p>
          </div>

          {/* Expiration Date */}
          <div>
            <div className='flex items-center gap-1 mb-3'>
              <input
                type="checkbox"
                id="updateExpirationToggle"
                checked={showExpirationDateInput}
                onChange={(e) => setShowExpirationDateInput(e.target.checked)}
                className="w-4 h-4 px-2 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="updateExpirationToggle" className="text-sm text-gray-700">
                Set Expiration Date
              </label>
            </div>
            <div className="relative border">
              <input
                type="date"
                disabled={!showExpirationDateInput}
                value={updateForm.expirationDate}
                onChange={(e) => onFormChange('expirationDate', e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Leave empty for no expiration</p>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Permissions
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* Read Permission */}
              <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={updateForm.readPermission}
                  onChange={(e) => onFormChange('readPermission', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="ml-3 flex items-center">
                  <EyeIcon className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Read</span>
                </div>
              </label>
              {/* Write Permission */}
              <label className="flex items-center p-3 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={updateForm.writePermission}
                  onChange={(e) => onFormChange('writePermission', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="ml-3 flex items-center">
                  <span className="text-sm font-medium text-gray-700">Write</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-md hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateTokenModal;